<script setup lang="ts">
/**
 * The text playground tab.
 *
 * Chat state, streaming and session handling now live in `features/chat`; what
 * remains here is configuration — which content kinds the input takes, which
 * model answers, and which completion parameters ride with the request.
 *
 * The transport is a computed: changing the settings or the auth token rebuilds
 * it, and `useChat` aborts any stream in flight when it does.
 */
import { computed, ref, watchEffect } from 'vue';
import { Alert } from '@asabdev/asab-ui';

import { ChatWindow, createOpenAITransport } from '@/features/chat';
import { useAuthStore } from '@/stores/auth';

import ChatSettings from '../shared/ChatSettings.vue';
// The playground's own picker, not the chat module's: this one renders the
// catalog's capability badges and pricing off the playground `Model` type, which
// features/chat cannot know about and should not.
import ModelPicker from '../shared/ModelPicker.vue';
import { chatSettingsToOptions } from '../../lib/openai-client';
import type { Model } from '../../types/models';
import type { ChatCompletionSettings } from '../../types/session';

const props = defineProps<{
  models: Model[];
  isLoadingModels: boolean;
}>();

const auth = useAuthStore();

const selectedModel = ref('');
const settings = ref<ChatCompletionSettings>({
  temperature: 1,
  maxTokens: null,
  topP: 1,
  frequencyPenalty: 0,
  presencePenalty: 0,
  stream: true,
  seed: null,
  stop: [],
  responseFormat: 'text',
  jsonSchema: '',
  tools: '',
  reasoningEffort: null,
  thinkingBudget: null,
});

/** The catalog arrives async; take the first model until the user picks one. */
watchEffect(() => {
  if (!selectedModel.value && props.models.length > 0) selectedModel.value = props.models[0].id;
});

const currentModel = computed(() => props.models.find((m) => m.id === selectedModel.value) ?? null);

const transport = computed(() =>
  createOpenAITransport({
    authConfig: { token: auth.token ?? '', isApiKey: false },
    // The only structural difference between this tab and a multimodal one.
    accept: ['text'],
    params: chatSettingsToOptions(settings.value),
  }),
);
</script>

<template>
  <div class="text-playground flex h-full bg-gray-50 dark:bg-gray-900">
    <div class="flex min-w-0 flex-1 flex-col">
      <Alert v-if="!isLoadingModels && models.length === 0" variant="warning" class="m-4">
        No text models available. Please check your API configuration.
      </Alert>

      <!-- This ephemeral notice is the strongest guarantee in the product, and
           still goes only this far: no agent means no trace payload, and the
           gateway's analytics event carries token counts and no content — but
           nothing upstream of us stops being upstream of us. Deliberately not
           the agent tab's copy, which must also disclose 24h of A2A session
           state. -->
      <ChatWindow
        class="min-h-0 flex-1"
        :transport="transport"
        :model="selectedModel"
        kind="playground"
        history
        allow-ephemeral
        ephemeral-notice="Nothing is stored on our servers. The model provider's own retention is outside our control."
        empty-state="Send a message to test a model"
      >
        <template #picker>
          <ModelPicker
            :model-value="selectedModel"
            :models="models"
            :is-loading="isLoadingModels"
            @update:model-value="(v: string) => (selectedModel = v)"
          />
        </template>
      </ChatWindow>
    </div>

    <ChatSettings :settings="settings" :model="currentModel" @update:settings="settings = $event" />
  </div>
</template>
